import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { gsap, reducedMotion } from '@/lib/anim'
import { Reveal, CountUp, ImageFrame } from '@/components/Reveal'
import { HelixStrand, HelixBand } from '@/components/Helix'
import { HeroVideo, ResponsiveImage } from '@/components/media'
import ContactSection from '@/components/ContactSection'
import { home, site } from '@/data/site-content'

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { hero, stats, intro, program, interlude, partners, quote, cta } = home

  useEffect(() => {
    const el = heroRef.current
    if (!el || reducedMotion) return
    const items = el.querySelectorAll('[data-hero-in]')
    gsap.set(items, { opacity: 0, y: 44 })
    const tl = gsap.timeline({ delay: 0.25 })
    tl.to(items, { opacity: 1, y: 0, duration: 1.4, ease: 'power3.out', stagger: 0.16 })
    const img = el.querySelector('[data-hero-img]')
    if (img) {
      gsap.fromTo(img, { scale: 1.12 }, { scale: 1, duration: 2.6, ease: 'power2.out' })
    }
    return () => {
      tl.kill()
    }
  }, [])

  return (
    <>
      {/* ————— HERO ————— */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden">
        <div className="absolute inset-0" data-hero-img>
          {hero.video ? (
            <HeroVideo
              mp4={hero.video.mp4}
              webm={hero.video.webm}
              poster={hero.video.poster}
              fallbackImage={hero.fallbackImage}
              alt="Hylix maize trial field"
              className="h-full w-full object-cover"
            />
          ) : (
            <ResponsiveImage
              src={hero.fallbackImage}
              alt="Hylix maize trial field at golden hour"
              loading="eager"
              decoding="sync"
              className="h-full w-full object-cover"
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, rgba(26,41,20,0.92) 0%, rgba(26,41,20,0.55) 50%, rgba(26,41,20,0.18) 100%)',
            }}
          />
        </div>

        <div className="pointer-events-none absolute bottom-0 right-[5%] top-0 hidden items-center xl:flex">
          <HelixStrand height={720} width={120} turns={3.5} stroke="#F3F5F1" opacity={0.16} rungs={11} />
        </div>

        <div className="container-hx relative pb-24 pt-40 md:pb-32">
          <div data-hero-in className="eyebrow" style={{ color: '#E8B85C' }}>
            {hero.eyebrow}
          </div>
          <h1
            data-hero-in
            className="mt-7 max-w-3xl text-[clamp(2.5rem,5.2vw,4.2rem)] font-light leading-[1.06] tracking-[-0.02em] text-[#F3F5F1] [text-wrap:balance]"
          >
            {hero.headline}{' '}
            <span className="block text-[#E8B85C]">{hero.highlight}</span>
          </h1>
          <p data-hero-in className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#F3F5F1]/75 md:text-lg">
            {hero.subheadline}
          </p>
          <div data-hero-in className="mt-10 flex flex-wrap gap-4">
            <Link to={hero.primaryCta.path} className="btn-primary" style={{ backgroundColor: '#C98E33', color: '#1E3018' }}>
              {hero.primaryCta.label}
            </Link>
            <Link to={hero.secondaryCta.path} className="btn-outline-cream">
              {hero.secondaryCta.label}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block">
          <div className="h-14 w-px overflow-hidden">
            <div
              className="h-full w-px"
              style={{
                background: 'linear-gradient(to bottom, transparent, #E8B85C)',
                animation: 'scrolldrip 2.2s ease-in-out infinite',
              }}
            />
          </div>
          <style>{`@keyframes scrolldrip { 0%{transform:translateY(-100%)} 60%,100%{transform:translateY(100%)} }`}</style>
        </div>
      </section>

      {/* ————— PROOF BAND ————— */}
      <section style={{ backgroundColor: '#E9EDE7' }}>
        <div className="container-hx grid grid-cols-2 gap-y-14 py-20 md:py-24 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="relative px-2 lg:px-8">
              {i > 0 && (
                <span className="absolute left-0 top-1 hidden h-14 w-px lg:block" style={{ backgroundColor: '#D9DED5' }} />
              )}
              <div className="font-mono-data text-5xl font-light tracking-[-0.03em] md:text-6xl" style={{ color: '#C98E33' }}>
                <CountUp value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-4 max-w-[180px] text-[12px] font-medium leading-relaxed" style={{ color: '#565650' }}>
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ————— WHO IS HYLIX ————— */}
      <section className="relative overflow-hidden py-24 md:py-36" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx grid items-center gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <div>
            <Reveal className="eyebrow">{intro.eyebrow}</Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.4vw,2.7rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]" >
              {intro.headline}{' '}
              <span style={{ color: '#4A6B3A' }}>{intro.highlight}</span>
            </Reveal>
            <Reveal as="p" delay={0.16} className="mt-7 max-w-xl leading-[1.85]" style={{ color: '#565650' }}>
              {intro.body}
            </Reveal>
            <Reveal delay={0.24} className="mt-9">
              <Link to={intro.cta.path} className="link-underline">
                {intro.cta.label}
              </Link>
            </Reveal>
          </div>
          <ImageFrame
            src={intro.image}
            alt={intro.imageCaption}
            ratio="4/3"
            caption={intro.imageCaption}
          />
        </div>
        <div className="container-hx mt-24 md:mt-32">
          <HelixBand height={48} turns={8} />
        </div>
      </section>

      {/* ————— BREEDING PROGRAM PREVIEW ————— */}
      <section className="py-24 md:py-36" style={{ backgroundColor: '#FAFBF9' }}>
        <div className="container-hx">
          <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <Reveal className="eyebrow">{program.eyebrow}</Reveal>
              <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.4vw,2.7rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
                {program.title}
              </Reveal>
              <Reveal as="p" delay={0.16} className="mt-6 max-w-xl leading-[1.85]" style={{ color: '#565650' }}>
                {program.body}
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to={program.cta.path} className="btn-outline">
                {program.cta.label}
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-px overflow-hidden md:grid-cols-3" style={{ backgroundColor: '#D9DED5', border: '1px solid #D9DED5' }}>
            {program.cards.map((p) => (
              <div key={p.title} className="group relative p-9 transition-colors duration-500 md:p-11">
                <div className="absolute inset-0 transition-colors duration-500 group-hover:bg-[#F3F5F1]" style={{ backgroundColor: '#FAFBF9' }} />
                <div className="relative">
                  <h3 className="text-lg font-medium" style={{ color: '#24391C' }}>
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed" style={{ color: '#565650' }}>
                    {p.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— FIELD IMAGE INTERLUDE ————— */}
      <section className="relative">
        <div className="h-[52vh] overflow-hidden md:h-[64vh]">
          <ImageFrame src={interlude.image} alt={interlude.caption} ratio="auto" className="h-full [&>div]:h-full [&>div]:!aspect-auto" />
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-end">
          <div className="container-hx pb-8">
            <span className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-white/80 [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]">
              {interlude.caption}
            </span>
          </div>
        </div>
      </section>

      {/* ————— PARTNERS ————— */}
      <section className="py-24 md:py-36" style={{ backgroundColor: '#E9EDE7' }}>
        <div className="container-hx">
          <Reveal className="eyebrow">{partners.eyebrow}</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.4vw,2.7rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            {partners.title}
          </Reveal>
          <Reveal as="p" delay={0.16} className="mt-6 max-w-xl leading-[1.85]" style={{ color: '#565650' }}>
            {partners.body}
          </Reveal>

          <div className="mt-14 grid gap-px overflow-hidden sm:grid-cols-2 lg:grid-cols-4" style={{ backgroundColor: '#D9DED5', border: '1px solid #D9DED5' }}>
            {partners.items.map((p) => (
              <div key={p.name} className="p-9 transition-colors duration-500 hover:!bg-[#F3F5F1]" style={{ backgroundColor: '#E4E9E1' }}>
                <div className="text-lg font-medium tracking-wide" style={{ color: '#24391C' }}>
                  {p.name}
                </div>
                <div className="mt-3 font-mono-data text-[11px] tracking-[0.06em]" style={{ color: '#96968A' }}>
                  {p.relationship}
                </div>
              </div>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to={partners.cta.path} className="link-underline">
              {partners.cta.label}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ————— FOUNDER QUOTE ————— */}
      <section className="relative overflow-hidden py-28 md:py-40" style={{ backgroundColor: '#24391C' }}>
        <div className="pointer-events-none absolute left-[4%] top-1/2 hidden -translate-y-1/2 lg:block">
          <HelixStrand height={420} width={80} turns={2.5} stroke="#8BA872" opacity={0.25} />
        </div>
        <div className="container-hx relative">
          <Reveal className="mx-auto max-w-3xl text-center">
            <div className="mx-auto mb-10 h-10 w-px" style={{ backgroundColor: '#C98E33' }} />
            <blockquote className="text-[clamp(1.5rem,3vw,2.3rem)] font-light leading-[1.35] tracking-[-0.01em] text-[#F3F5F1]">
              “{quote.text}”
            </blockquote>
            <div className="mt-10 font-mono-data text-[11px] uppercase tracking-[0.22em]" style={{ color: '#8BA872' }}>
              — {quote.attribution}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— WORK WITH US (final CTA) ————— */}
      <section id="work-with-us" className="scroll-mt-[72px] py-24 md:py-36" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal className="eyebrow">{cta.eyebrow}</Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.9rem,3.6vw,2.9rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
              {cta.title}
            </Reveal>
            <Reveal as="p" delay={0.16} className="mx-auto mt-7 max-w-2xl leading-[1.85]" style={{ color: '#565650' }}>
              {cta.body}
            </Reveal>
            <Reveal delay={0.24} className="mt-11 flex flex-wrap justify-center gap-4">
              <Link to={cta.primary.path} className="btn-primary">
                {cta.primary.label}
              </Link>
              <Link to={cta.secondary.path} className="btn-outline">
                {cta.secondary.label}
              </Link>
            </Reveal>
            <Reveal delay={0.3} className="mt-10 font-mono-data text-[11px] uppercase tracking-[0.2em]" >
              <span style={{ color: '#96968A' }}>{site.email} · Based in South Africa</span>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— CONTACT (final section) ————— */}
      <ContactSection />
    </>
  )
}
